import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2, Lock, Copy, Globe, Sparkles } from 'lucide-react';
import { API } from '../../lib/constants';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';
import { Field } from '../AuthShell';
import { PoweredBy } from '../PoweredBy';
import { copyText } from '../../lib/clipboard';

export const BrandingSettings = ({ tenant, onUpgrade }) => {
  const { checkAuth } = useAuth();
  const isPaid = tenant?.plan === 'paid';
  const [form, setForm] = useState({
    welcome_message: tenant?.branding?.welcome_message || '',
    hide_branding: !!tenant?.branding?.hide_branding,
    custom_domain: tenant?.custom_domain || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      welcome_message: tenant?.branding?.welcome_message || '',
      hide_branding: !!tenant?.branding?.hide_branding,
      custom_domain: tenant?.custom_domain || '',
    });
  }, [tenant]);

  const publicUrl = `${window.location.origin}/t/${tenant?.slug}`;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/branding`, form);
      await checkAuth();
      toast.success('Branding saved');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async () => {
    (await copyText(publicUrl)) && toast.success('Portal link copied');
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6" data-testid="branding-settings">
      <div className="xl:col-span-3 space-y-6">
        <div className="card p-6 space-y-5">
          <div>
            <h2 className="h-display text-lg">Public portal</h2>
            <p className="text-sm text-slate-400 mt-1">Share this link or print it as a QR at the venue.</p>
          </div>
          <div className="flex items-center gap-2">
            <code data-testid="tenant-public-url" className="flex-1 text-xs bg-ink border-2 border-line px-3 py-3 text-cyan truncate">{publicUrl}</code>
            <button data-testid="copy-public-url-btn" onClick={copyUrl} className="icon-btn !w-11 !h-11 !border-2"><Copy className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <div>
            <h2 className="h-display text-lg">Branding</h2>
            <p className="text-sm text-slate-400 mt-1">Customize what attendees see on your registration and gallery pages.</p>
          </div>

          <Field label="Welcome message">
            <textarea
              data-testid="branding-welcome-input"
              className="input min-h-[104px] resize-y !font-body"
              maxLength={300}
              value={form.welcome_message}
              onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
            />
            <p className="font-mono text-[10px] text-slate-500 text-right">{form.welcome_message.length}/300</p>
          </Field>

          <Field label="Custom domain (saved for future use)">
            <div className="relative">
              <Globe className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="branding-domain-input"
                className="input !pl-10"
                placeholder="photos.yourbrand.com"
                value={form.custom_domain}
                onChange={(e) => setForm({ ...form, custom_domain: e.target.value })}
              />
            </div>
            <p className="font-mono text-[10px] text-slate-500">Domain routing isn't live yet — we store it so it's ready when it is.</p>
          </Field>

          <div className={`flex items-start justify-between gap-4 p-4 border-2 ${isPaid ? 'border-lime bg-lime/5' : 'border-line bg-ink'}`}>
            <div className="min-w-0">
              <p className="font-display font-bold uppercase tracking-wider text-sm text-white flex items-center gap-2 flex-wrap">
                Hide "Powered by" footer
                {!isPaid && <span className="badge-warning"><Lock className="w-3 h-3" /> Pro</span>}
              </p>
              <p className="text-xs text-slate-400 mt-1">Remove Spotfie branding from all your public pages — included with the Pro Event Pass.</p>
              {!isPaid && (
                <button data-testid="branding-upgrade-link" onClick={onUpgrade} className="font-mono text-[11px] uppercase tracking-wider text-cyan hover:underline mt-3 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Get the Pro Event Pass
                </button>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.hide_branding}
              disabled={!isPaid}
              data-testid="branding-hide-toggle"
              onClick={() => setForm({ ...form, hide_branding: !form.hide_branding })}
              className={`relative shrink-0 w-12 h-7 border-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${form.hide_branding ? 'bg-lime border-lime' : 'bg-ink border-line'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 transition-transform ${form.hide_branding ? 'translate-x-5 bg-ink' : 'bg-slate-500'}`} />
            </button>
          </div>

          <button data-testid="tenant-branding-save-button" onClick={save} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="xl:col-span-2">
        <div className="card-brutal sticky top-24" data-testid="branding-preview">
          <div className="px-5 py-3 border-b-2 border-line flex items-center justify-between">
            <p className="label">Live preview</p>
            <span className="font-mono text-[10px] text-slate-500">/t/{tenant?.slug}</span>
          </div>
          <div className="bg-ink p-5">
            <div className="bg-surface border-2 border-line">
              <div className="h-10 border-b-2 border-line flex items-center px-3 gap-2">
                <span className="w-5 h-5 bg-cyan text-ink text-[10px] font-display font-bold flex items-center justify-center">{tenant?.name?.charAt(0)}</span>
                <span className="font-display font-bold uppercase text-xs text-white truncate">{tenant?.name}</span>
              </div>
              <div className="p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-cyan">Event photo delivery</p>
                <p className="h-display text-base mt-1">{tenant?.name}</p>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed" data-testid="preview-welcome-message">{form.welcome_message || '—'}</p>
                <div className="mt-4 space-y-2">
                  <div className="h-7 border-2 border-line" />
                  <div className="h-7 border-2 border-line" />
                  <div className="h-8 bg-cyan" />
                </div>
              </div>
              <PoweredBy hidden={isPaid && form.hide_branding} className="!py-3 !text-[9px] pointer-events-none" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
